import { useEffect, useState } from 'react'

/**
 * Light / dark, with "system" as the default rather than a hardcoded light.
 *
 * Tailwind is configured with `darkMode: 'class'`, so the whole mechanism is one class on
 * `<html>`. The initial paint is handled by an inline script in `index.html`; without it
 * a dark-mode user gets a white flash on every page load, which is worse than no dark
 * mode at all.
 */
export type Theme = 'light' | 'dark' | 'system'

const KEY = 'fmcg.theme'

function prefersDark() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function applyTheme(theme: Theme): void {
  const dark = theme === 'dark' || (theme === 'system' && prefersDark())
  document.documentElement.classList.toggle('dark', dark)
}

export function readTheme(): Theme {
  try {
    const stored = localStorage.getItem(KEY)
    return stored === 'light' || stored === 'dark' ? stored : 'system'
  } catch {
    return 'system'
  }
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(readTheme)

  useEffect(() => {
    applyTheme(theme)
    if (theme !== 'system') return

    // Follow the OS while set to "system" -- otherwise the page stays light until reload
    // when someone flips their machine to dark at sunset.
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => applyTheme('system')
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [theme])

  const setTheme = (next: Theme) => {
    try {
      if (next === 'system') localStorage.removeItem(KEY)
      else localStorage.setItem(KEY, next)
    } catch {
      /* storage unavailable; the choice just will not survive a reload */
    }
    setThemeState(next)
  }

  const isDark = theme === 'dark' || (theme === 'system' && prefersDark())

  return { theme, setTheme, isDark, toggle: () => setTheme(isDark ? 'light' : 'dark') }
}
